import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { MdError } from 'react-icons/md';
import { AppContext } from '../../App';
import OTPInput from '../../components/OTPInput';
import SimpleFooter from '../../components/SimpleFooter';
import SimpleHeader from '../../components/SimpleHeader';
import { CAC_TYPE } from '../../res/Constants';
import { login } from '../../service/AccountService';
import './login.scss';

export default function TwoFactorVerify(props) {
    const history = useHistory();
    const location = useLocation();
    const appContext = React.useContext(AppContext);
    const [otp, setOtp] = React.useState('');
    const [verificationError, setVerificationError] = React.useState(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    // Get credentials and session data from location state
    const username = location.state?.username;
    const password = location.state?.password;
    const rememberMe = location.state?.rememberMe;

    // Note: We don't redirect immediately if data is missing
    // to allow time for the location state to be populated

    const handleVerify = React.useCallback(() => {
        if (otp.length !== 6) {
            setVerificationError('Please enter all 6 digits');
            return;
        }

        setVerificationError(null);
        setIsSubmitting(true);

        // Call login with username, password and OTP
        // Note: remember-me is sent ONLY in this final login call with OTP
        const formData = {
            username: username,
            password: password,
            otp: otp
        };

        // Add remember-me only if user selected it
        if (rememberMe) {
            formData['remember-me'] = 'on';
        }

        login(formData, (result) => {
            setIsSubmitting(false);

            if (result.status === 200) {
                appContext.onCacTypeChange(CAC_TYPE.CROP);
                history.replace('/nav');
            } else {
                setVerificationError('The code is invalid or expired, try again.');
                setOtp(''); // Clear OTP on error
            }
        });
    }, [otp, username, password, rememberMe, appContext, history]);

    // Auto-submit when OTP is complete
    React.useEffect(() => {
        if (otp.length === 6 && !isSubmitting) {
            handleVerify();
        }
    }, [otp, isSubmitting, handleVerify]);

    return (
        <div className='h-100 login-page'>
            <SimpleHeader />
            <Container>
                <ContentBox>
                    <Title>Access your authenticator app</Title>
                    <Subtitle>
                        Use your authenticator app to get the code.
                    </Subtitle>

                    <OTPInputContainer>
                        <OTPInput
                            value={otp}
                            onChange={setOtp}
                            numInputs={6}
                            disabled={isSubmitting}
                        />
                        {verificationError && (
                            <ErrorMessage>
                                <MdError size={18} />
                                <span>{verificationError}</span>
                            </ErrorMessage>
                        )}
                    </OTPInputContainer>
                </ContentBox>
            </Container>
            <SimpleFooter {...props} />
        </div>
    );
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    background-color: #f5f5f5;
    height: 83vh;

    @media (max-width: 768px) {
        padding: 20px 15px;
    }
`;

const ContentBox = styled.div`
    padding: 40px 20px;
    max-width: 450px;
    width: 100%;
    text-align: center;

    @media (max-width: 768px) {
        padding: 20px 15px;
    }
`;

const Title = styled.h2`
    font-size: 24px;
    font-weight: 600;
    color: #333;
    margin-bottom: 10px;

    @media (max-width: 768px) {
        font-size: 20px;
    }
`;

const Subtitle = styled.p`
    font-size: 14px;
    color: #666;
    margin-bottom: 30px;

    @media (max-width: 768px) {
        font-size: 13px;
        margin-bottom: 20px;
    }
`;

const OTPInputContainer = styled.div`
    margin: 30px 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;

    @media (max-width: 768px) {
        margin: 20px 0;
    }
`;

const ErrorMessage = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #dc3545;
    font-size: 13px;
    margin-top: 15px;
    text-align: center;

    @media (max-width: 768px) {
        font-size: 12px;
        gap: 6px;
    }
`;